import * as Dialog from '@radix-ui/react-dialog';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useEffect, useState } from 'react';
import { useTransition } from 'react-transition-state';

import * as styles from './modal.css';

// a QuickSearch modal that can be used to display a QuickSearch command
// it has a smooth animation and can be closed by clicking outside of the modal

export interface QuickSearchModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

const animationTimeout = 120; // Reduced for snappier feel

export const QuickSearchModal = ({
  onOpenChange,
  open,
  children,
}: React.PropsWithChildren<QuickSearchModalProps>) => {
  const [{ status }, toggle] = useTransition({
    timeout: animationTimeout,
  });

  const [customPosition, setCustomPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    toggle(open);

    // Check if we have a stored position for the search modal
    try {
      const positionData = localStorage.getItem('quickSearchPosition');
      if (positionData) {
        const position = JSON.parse(positionData);
        setCustomPosition(position);
        // Clear the stored position after using it
        setTimeout(() => {
          localStorage.removeItem('quickSearchPosition');
        }, animationTimeout);
      } else {
        setCustomPosition(null);
      }
    } catch (e) {
      console.error('Error parsing quickSearchPosition:', e);
      setCustomPosition(null);
    }
  }, [open]);

  return (
    <Dialog.Root modal open={status !== 'exited'} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.modalOverlay} />
        <div
          className={styles.modalContentWrapper}
          data-custom-position={!!customPosition}
          style={
            customPosition
              ? assignInlineVars({
                '--modal-top': `${customPosition.top}px`,
                '--modal-left': `${customPosition.left}px`,
                '--modal-width': `${customPosition.width}px`,
              })
              : undefined
          }
        >
          <Dialog.Content
            style={assignInlineVars({
              [styles.animationTimeout]: `${animationTimeout}ms`,
            })}
            className={styles.modalContent}
            data-state={status}
          >
            {children}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
